import { type ArtifactListingEntry, PLUGIN_FOLDER_TYPE } from '@crowdex/agent-contracts/api';
import {
  AudioLines,
  Blocks,
  FileText,
  Globe,
  Image,
  type LucideIcon,
  Shapes,
  Video,
} from 'lucide-react';
import type { PluginPanel } from '@/features/plugins/plugin-workbench';

/** A filter pill of the Library: the artifacts of one kind, and how many there are. */
export type LibraryCategory = { key: string; label: string; count: number };

/** The core's document types, which the Library groups as one kind ("Docs"). */
const DOCUMENT_TYPES = new Set(['core/document', 'core/pdf', 'core/html']);

/** The core's own file types: their kind and glyph. */
const CORE_KINDS: Record<string, { label: string; icon: LucideIcon }> = {
  'core/image': { label: 'Images', icon: Image },
  'core/video': { label: 'Videos', icon: Video },
  'core/audio': { label: 'Audio', icon: AudioLines },
  [PLUGIN_FOLDER_TYPE]: { label: 'Plugins', icon: Blocks },
};

/** "Design" → "Designs", "Spreadsheet" → "Spreadsheets", "Gallery" → "Galleries". */
function plural(title: string): string {
  if (/s$/i.test(title)) return title;
  if (/[^aeiou]y$/i.test(title)) return `${title.slice(0, -1)}ies`;
  return `${title}s`;
}

/** The kind an artifact is listed under: its type, in the plural; the core's documents together. */
export function categoryOf(artifact: Pick<ArtifactListingEntry, 'type' | 'typeTitle'>): {
  key: string;
  label: string;
} {
  if (DOCUMENT_TYPES.has(artifact.type)) return { key: 'Docs', label: 'Docs' };
  const label = CORE_KINDS[artifact.type]?.label ?? plural(artifact.typeTitle);
  return { key: label, label };
}

/** The glyph of an artifact of the core: its thumbnail when it has no preview. */
export function coreGlyph(type: string): LucideIcon {
  if (DOCUMENT_TYPES.has(type)) return type === 'core/html' ? Globe : FileText;
  return CORE_KINDS[type]?.icon ?? Shapes;
}

/** What a plugin provides rather than the user made: a template, or a file a plugin ships. */
export function fromPlugin(artifact: Pick<ArtifactListingEntry, 'template' | 'space'>): boolean {
  return artifact.template || artifact.space === 'plugin';
}

/**
 * Each file once (a workspace's file is indexed by every conversation that listed it), kept as
 * last changed; the last changed first.
 */
function latestOnce(artifacts: readonly ArtifactListingEntry[]): ArtifactListingEntry[] {
  const latest = new Map<string, ArtifactListingEntry>();
  for (const artifact of artifacts) {
    const owner = artifact.space === 'plugin' ? artifact.plugin : (artifact.workspace ?? '');
    const key = `${artifact.space}:${owner}:${artifact.path}`;
    const known = latest.get(key);
    if (known === undefined || Date.parse(artifact.modifiedAt) > Date.parse(known.modifiedAt))
      latest.set(key, artifact);
  }
  return [...latest.values()].toSorted(
    (left, right) => Date.parse(right.modifiedAt) - Date.parse(left.modifiedAt),
  );
}

/**
 * The user's artifacts, which the Library lists first (plan §2.11, D26): never a template nor
 * what a plugin ships (its design systems, its examples), listed apart (`pluginArtifacts`).
 */
export function libraryArtifacts(
  artifacts: readonly ArtifactListingEntry[],
): ArtifactListingEntry[] {
  return latestOnce(artifacts.filter((artifact) => !fromPlugin(artifact)));
}

/** What the plugins provide (templates, design systems…), each once, apart from the user's own. */
export function pluginArtifacts(
  artifacts: readonly ArtifactListingEntry[],
): ArtifactListingEntry[] {
  return latestOnce(artifacts.filter(fromPlugin));
}

/** What one app provides, in the Library's "From your plugins". */
export type PluginGroup = {
  plugin: string;
  title: string;
  /** Its kinds, with how many of each: "Templates 1", "Design systems 151". */
  kinds: { label: string; count: number }[];
  artifacts: ArtifactListingEntry[];
};

/** The kind a plugin's entry is counted under in its group: "Templates", else its type's. */
function groupKind(artifact: ArtifactListingEntry): string {
  return artifact.template ? 'Templates' : categoryOf(artifact).label;
}

/** What a card of the plugins' sections says it is: "Template", else its type. */
export function pluginKindOf(
  artifact: Pick<ArtifactListingEntry, 'template' | 'type' | 'typeTitle'>,
): string {
  return artifact.template ? 'Template' : kindOf(artifact);
}

/**
 * What the plugins provide, by app: in the order of the apps (`apps`), then the others by name.
 * In a group its templates first, then by kind and title.
 */
export function pluginGroups(
  artifacts: readonly ArtifactListingEntry[],
  apps: readonly Pick<PluginPanel, 'plugin' | 'title'>[] = [],
): PluginGroup[] {
  const byPlugin = new Map<string, ArtifactListingEntry[]>();
  for (const artifact of artifacts)
    byPlugin.set(artifact.plugin, [...(byPlugin.get(artifact.plugin) ?? []), artifact]);
  const rank = (plugin: string) => {
    const index = apps.findIndex((app) => app.plugin === plugin);
    return index === -1 ? apps.length : index;
  };
  return [...byPlugin]
    .map(([plugin, entries]): PluginGroup => {
      const counts = new Map<string, number>();
      for (const artifact of entries)
        counts.set(groupKind(artifact), (counts.get(groupKind(artifact)) ?? 0) + 1);
      return {
        plugin,
        title:
          apps.find((app) => app.plugin === plugin)?.title ?? entries[0]?.pluginTitle ?? plugin,
        kinds: [...counts]
          .map(([label, count]) => ({ label, count }))
          .toSorted(
            (left, right) =>
              Number(right.label === 'Templates') - Number(left.label === 'Templates') ||
              right.count - left.count ||
              left.label.localeCompare(right.label),
          ),
        artifacts: entries.toSorted(
          (left, right) =>
            Number(right.template) - Number(left.template) ||
            kindOf(left).localeCompare(kindOf(right)) ||
            left.title.localeCompare(right.title),
        ),
      };
    })
    .toSorted(
      (left, right) =>
        rank(left.plugin) - rank(right.plugin) || left.title.localeCompare(right.title),
    );
}

/** What a card says an artifact is: its type, the core's documents as "Doc". */
export function kindOf(artifact: Pick<ArtifactListingEntry, 'type' | 'typeTitle'>): string {
  return DOCUMENT_TYPES.has(artifact.type) ? 'Doc' : artifact.typeTitle;
}

/**
 * The Library's filter pills: one per kind present, in the order of the apps (`order`, their
 * kinds), then the others, the most common first.
 */
export function libraryCategories(
  artifacts: readonly ArtifactListingEntry[],
  order: readonly string[] = [],
): LibraryCategory[] {
  const counts = new Map<string, LibraryCategory>();
  for (const artifact of artifacts) {
    const { key, label } = categoryOf(artifact);
    const entry = counts.get(key) ?? { key, label, count: 0 };
    entry.count += 1;
    counts.set(key, entry);
  }
  const rank = (key: string) => {
    const index = order.indexOf(key);
    return index === -1 ? order.length : index;
  };
  return [...counts.values()].toSorted(
    (left, right) =>
      rank(left.key) - rank(right.key) ||
      right.count - left.count ||
      left.label.localeCompare(right.label),
  );
}

/** The kinds of an app's artifacts: the pills its tile selects. */
export function appCategoryKeys(panel: Pick<PluginPanel, 'artifactTypes'>): string[] {
  return [
    ...new Set(
      (panel.artifactTypes ?? []).map(
        (type) => categoryOf({ type: type.type, typeTitle: type.title }).key,
      ),
    ),
  ];
}

/** What "New …" of an app makes, in lower case: its first artifact type ("New design"). */
export function appNoun(panel: Pick<PluginPanel, 'artifactTypes' | 'title'>): string {
  return (panel.artifactTypes?.[0]?.title ?? panel.title).toLowerCase();
}

/** "now", "8 min", "1 h", "yesterday", "Mon", "Oct 18": how long ago, as short as it reads. */
export function shortAgo(iso: string, now = Date.now()): string {
  const at = Date.parse(iso);
  if (!Number.isFinite(at)) return '';
  const minutes = Math.floor((now - at) / 60_000);
  if (minutes < 1) return 'now';
  if (minutes < 60) return `${minutes} min`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours} h`;
  const date = new Date(at);
  const today = new Date(now);
  const yesterday = new Date(now);
  yesterday.setDate(today.getDate() - 1);
  if (date.toDateString() === yesterday.toDateString()) return 'yesterday';
  if (now - at < 7 * 86_400_000) return date.toLocaleDateString('en-US', { weekday: 'short' });
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}
